import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchVendors } from "../slices/vendorSlice";
import { Button, Switch, Spin } from "antd";
import noDataImage from "../assets/icons/nodataimage.svg";
import DashboardMetrics from "../components/VendorMetrics";
import CreateVendorModal from "../components/CreateVendorModal";
import "./Vendors.css";
import { updateVendorStatus } from "../services/api";
import Swal from "sweetalert2";
import moment from "moment";
import TopBar from "./../components/TopBar";
import { ShimmerDiv, ShimmerTable } from "shimmer-effects-react";

const Vendors = () => {
  const dispatch = useDispatch();
  const vendorsFromStore = useSelector((state) => state.vendors?.data || []);
  const [vendors, setVendors] = useState(vendorsFromStore);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  const convertToCSV = (data) => {
    const headers = [
      "No.",
      "Full Name",
      "Email ID",
      "Mobile No.",
      "Category",
      "Date",
      "isSubscribed",
      "Status",
    ];

    const csvRows = [];
    csvRows.push(headers.join(",")); // Add headers to the CSV rows

    data.forEach((vendor, index) => {
      const row = [
        index + 1, // No.
        vendor.name || "N/A", // Full Name
        vendor.email || "N/A", // Email ID
        vendor.phoneNumber || "N/A", // Mobile No.
        vendor.category?.name || "N/A", // Category
        moment(vendor.createdAt).format("DD-MM-YY"), // Date
        vendor.isSubscribed.toString(), // isSubscribed
        vendor.status, // Status (active/deactive)
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };
  const handleDownloadCSV = () => {
    const csvData = convertToCSV(vendors); // Use the `vendors` state that contains the filtered data
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `vendors_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filters and sorting state
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "asc",
  }); // Default to "createdAt" in ascending order

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchVendors()).finally(() => setIsLoading(false)); // Set loading to false after fetching data
  }, [dispatch]);

  const totalRevenue = vendorsFromStore
    .reduce(
      (sum, vendor) =>
        sum +
        (vendor.totalPricePaid.active +
          vendor.totalPricePaid.expired +
          vendor.totalPricePaid.inactive || 0),
      0
    )
    .toFixed(0);
  const totalSubscribers = vendorsFromStore.reduce(
    (sum, vendor) => sum + (vendor.isSubscribed ? 1 : 0 || 0),
    0
  );

  useEffect(() => {
    applyFiltersAndSorting();
  }, [vendorsFromStore, filter, dateFilter, sortConfig, searchText]);

  const applyFiltersAndSorting = () => {
    let filteredVendors = vendorsFromStore;
    if (searchText) {
      filteredVendors = filteredVendors.filter((user) =>
        user.name.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    // Apply status filter
    if (filter === "active") {
      filteredVendors = filteredVendors.filter(
        (vendor) => vendor.status === "active"
      );
    } else if (filter === "deactive") {
      filteredVendors = filteredVendors.filter(
        (vendor) => vendor.status === "deactive"
      );
    }

    // Apply date filter
    const today = moment();
    filteredVendors = filteredVendors.filter((vendor) => {
      const vendorDate = moment(vendor.createdAt);
      switch (dateFilter) {
        case "today":
          return vendorDate.isSame(today, "day");
        case "week":
          return vendorDate.isSame(today, "week");
        case "month":
          return vendorDate.isSame(today, "month");
        case "year":
          return vendorDate.isSame(today, "year");
        default:
          return true;
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filteredVendors = filteredVendors.sort((a, b) => {
        if (
          sortConfig.key === "createdAt" ||
          sortConfig.key === "name" ||
          sortConfig.key === "email"
        ) {
          return sortConfig.direction === "asc"
            ? a[sortConfig.key].localeCompare(b[sortConfig.key])
            : b[sortConfig.key].localeCompare(a[sortConfig.key]);
        } else {
          return sortConfig.direction === "asc"
            ? a[sortConfig.key] - b[sortConfig.key]
            : b[sortConfig.key] - a[sortConfig.key];
        }
      });
    }

    setVendors(filteredVendors);
  };

  const handleSwitchChange = (index) => {
    const vendor = vendors[index];
    const newStatus = vendor.status === "active" ? "deactive" : "active";

    Swal.fire({
      title: "Are you sure?",
      text: `You are about to set this vendor to ${newStatus}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const newVendors = vendors.map((vendor, idx) =>
          idx === index ? { ...vendor, status: newStatus } : vendor
        );
        setVendors(newVendors);

        updateVendorStatus(vendor._id, newStatus)
          .then(() => {
            Swal.fire(
              "Updated!",
              `Vendor status has been updated to ${newStatus}.`,
              "success"
            );
          })
          .catch((error) => {
            console.error("Error updating vendor status:", error);
            Swal.fire(
              "Error!",
              "There was an error updating the status. Please try again.",
              "error"
            );
            setVendors((prevVendors) =>
              prevVendors.map((vendor, idx) =>
                idx === index ? { ...vendor, status: vendor.status } : vendor
              )
            );
          });
      }
    });
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const updateVendorData = () => {
    dispatch(fetchVendors());
  };

  // Sorting function
  const sortVendors = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };
  // Pagination logic
  const totalPages = Math.ceil(vendors.length / itemsPerPage);
  const handlePreviousPage = () =>
    currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const paginatedVendors = vendors.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Show loading animation or error message if needed
  if (isLoading) {
    return (
      <>
        <div className="p-5 grid gap-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 items-center justify-between">
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
        </div>
        <ShimmerTable mode="dark" row={7} col={9} border={0} borderColor={"#cbd5e1"} rounded={0.25} rowGap={16} colPadding={[10, 5, 10, 5]} />
      </>
    );
  }
  return (<>
    <TopBar searchPlaceholder="Search Vendors Here" onSearchChange={handleSearchChange} />
    <div className="p-6">
      <div className="vendors-container">
        {/* Metrics Section */}
        <div className="metrics-section">
          <DashboardMetrics
            title="Total Vendors"
            count={vendorsFromStore.length.toString()}
          />
          <DashboardMetrics title="Total Subscribers" count={totalSubscribers} />
          <DashboardMetrics
            title="Total Subcategories"
            count={vendorsFromStore?.[0]?.totalSubcategories ?? 0}
          />
          <DashboardMetrics title="Total Revenue" count={"€ " + totalRevenue} />
        </div>
        <div className="search-create-section">
          <div className="dropdown-buttons">
            {/* User Status Dropdown */}
            <div className="users-dropdown">
              <button className="users-dropdown-button">
                {filter === "all"
                  ? "Vendor Status"
                  : filter === "active"
                    ? "Activated Vendors"
                    : "Deactivated Vendors"}
                <div className="users-dropdown-content">
                  <a
                    href="#"
                    className={filter === "all" ? "active" : ""}
                    onClick={() => setFilter("all")}
                  >
                    All Vendors
                  </a>
                  <a
                    href="#"
                    className={filter === "active" ? "active" : ""}
                    onClick={() => setFilter("active")}
                  >
                    Activated Vendors
                  </a>
                  <a
                    href="#"
                    className={filter === "deactive" ? "active" : ""}
                    onClick={() => setFilter("deactive")}
                  >
                    Deactivated Vendors
                  </a>
                </div>
              </button>
            </div>


            {/* Date Filter Dropdown */}
            <div className="users-dropdown">
              <button className="users-dropdown-button">
                {dateFilter === "all"
                  ? "Date Filter"
                  : dateFilter === "today"
                    ? "Today"
                    : dateFilter === "week"
                      ? "This Week"
                      : dateFilter === "month"
                        ? "This Month"
                        : "This Year"}
                <div className="users-dropdown-content">
                  <a
                    href="#"
                    className={dateFilter === "all" ? "active" : ""}
                    onClick={() => setDateFilter("all")}
                  >
                    All Dates
                  </a>
                  <a
                    href="#"
                    className={dateFilter === "today" ? "active" : ""}
                    onClick={() => setDateFilter("today")}
                  >
                    Today
                  </a>
                  <a
                    href="#"
                    className={dateFilter === "week" ? "active" : ""}
                    onClick={() => setDateFilter("week")}
                  >
                    This Week
                  </a>
                  <a
                    href="#"
                    className={dateFilter === "month" ? "active" : ""}
                    onClick={() => setDateFilter("month")}
                  >
                    This Month
                  </a>
                  <a
                    href="#"
                    className={dateFilter === "year" ? "active" : ""}
                    onClick={() => setDateFilter("year")}
                  >
                    This Year
                  </a>
                </div>
              </button>
            </div>
          </div>
          <Button type="primary" className="create-button" onClick={openModal}>
            Create New Vendor
          </Button>
        </div>
        {vendors.length > 0 ? (
          <div className="vendor-table-container">
            <table className="vendor-table">
              <thead>
                <tr>
                  <th className="header-cell">No.</th>
                  <th className="header-cell" onClick={() => sortVendors("name")}>
                    Full Name
                    {sortConfig.key === "name" && (
                      <span>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                  <th
                    className="header-cell"
                    onClick={() => sortVendors("email")}
                  >
                    Email ID
                    {sortConfig.key === "email" && (
                      <span>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                  <th className="header-cell">Mobile No.</th>
                  <th className="header-cell">Category</th>
                  <th
                    className="header-cell"
                    onClick={() => sortVendors("createdAt")}
                  >
                    Date
                    {sortConfig.key === "createdAt" && (
                      <span>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                  <th className="header-cell">isSubscribed</th>
                  <th className="header-cell">Activate/Deactivate</th>
                  <th className="header-cell">More</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVendors.map((vendor, index) => (
                  <tr key={vendor._id}>
                    <td className="body-cell">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="body-cell">{vendor.name}</td>
                    <td className="body-cell">{vendor.email}</td>
                    <td className="body-cell">{vendor.phoneNumber}</td>
                    <td className="body-cell">{vendor.category.name}</td>
                    <td className="body-cell">
                      {moment(vendor.createdAt).format("DD-MM-YY")}
                    </td>
                    <td className="body-cell">
                      {vendor.isSubscribed.toString()}
                    </td>
                    <td className="body-cell">
                      <Switch
                        checked={vendor.status === "active"}
                        onChange={() => handleSwitchChange(index)}
                        style={{
                          backgroundColor:
                            vendor.status === "active" ? "#00BD79" : "#798593",
                        }}
                      />
                    </td>
                    <td className="body-cell">
                      <Link to={`/vendors/${vendor._id}`} className="more-link">
                        More..
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-container mt-4 mx-auto justify-between px-1">
              <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                &lt; Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next &gt;
              </button>
            </div>
            <div className="download-report">
              <button className="download-link" onClick={handleDownloadCSV}>
                Download Report
              </button>
            </div>
          </div>
        ) : (
          <div className="no-data-section">
            <img src={noDataImage} alt="No data" className="no-data-image" />
            <div className="no-data-title">No Vendors to show right now!</div>
            <div className="no-data-description">
              We don't have any vendor's info to show here. Please add vendors
              first and then check their details here.
            </div>
          </div>
        )}

        {isModalOpen && (
          <div className="modal-overlay" onClick={toggleModal}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <CreateVendorModal
                closeModal={closeModal}
                updateVendorData={updateVendorData}
              />
            </div>
          </div>
        )}
      </div>
    </div></>
  );
};

export default Vendors;
